import { createContext, useContext, useMemo, type ReactElement, type ReactNode } from 'react';

import { formatMillimesAsNumber, formatMillimesAsTnd } from './currency';
import { DEFAULT_LOCALE, type Locale } from './locales';
import { type MessageKey } from './messages/fr';
import { directionFor } from './rtl';
import { CATALOGUES, interpolate, type MessageParams } from './translate';

export type { MessageParams } from './translate';

export interface I18nContextValue {
  readonly locale: Locale;
  readonly direction: 'ltr' | 'rtl';
  readonly t: (key: MessageKey, params?: MessageParams) => string;
  readonly formatMoney: (amountMillimes: number) => string;
  readonly formatNumber: (amountMillimes: number) => string;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

export interface I18nProviderProps {
  readonly locale?: Locale;
  readonly children: ReactNode;
}

/**
 * Provides locale, direction and translation to every ui-kit component.
 *
 * Deliberately does not set `dir`/`lang` on `<html>` itself — that is an
 * application-level concern (the root layout owns the document), and a
 * library component reaching outside its own subtree to mutate global DOM
 * state is a common source of surprising behaviour in a monorepo with
 * several apps. `direction` is exposed so the application CAN set it, and
 * ui-kit components use it locally via the `dir` attribute where needed
 * (e.g. an icon that must mirror).
 */
export function I18nProvider({
  locale = DEFAULT_LOCALE,
  children,
}: I18nProviderProps): ReactElement {
  const value = useMemo<I18nContextValue>(() => {
    const catalogue = CATALOGUES[locale];

    return {
      locale,
      direction: directionFor(locale),
      t: (key, params) => interpolate(catalogue[key], params),
      formatMoney: (amountMillimes) => formatMillimesAsTnd(amountMillimes, locale),
      formatNumber: (amountMillimes) => formatMillimesAsNumber(amountMillimes, locale),
    };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);

  if (context === undefined) {
    throw new Error(
      'useI18n() was called outside an <I18nProvider>. Every ui-kit component that renders ' +
        'translated text or formatted money must be mounted under one.',
    );
  }

  return context;
}
